/* One lifecycle and mutation queue for Concept cart actions and the SKRT gift coordinator.
 * begin/end announce shopper operations, including their debounce period. run only serializes
 * work; the gift coordinator uses run without announcing an external shopper operation.
 */
(function (root) {
  'use strict';
  if (!root || root.skrtCartOperations) return;

  const pending = new Set();
  let sequence = 0;
  let localQueue = Promise.resolve();

  function announce(type, id) {
    root.document.dispatchEvent(new CustomEvent(type, { detail: { id } }));
  }

  function begin() {
    const id = 'skrt-cart-' + Date.now() + '-' + (++sequence);
    pending.add(id);
    announce('skrt:cart-operation-start', id);
    return id;
  }

  function end(id) {
    if (!pending.delete(id)) return;
    announce('skrt:cart-operation-end', id);
  }

  function run(task, signal) {
    if (typeof task !== 'function') return Promise.reject(new TypeError('Cart task must be a function'));
    const waiting = new AbortController();
    let acquired = false;
    let timer;
    let rejectWaiting;
    const aborted = new Promise((resolve, reject) => { rejectWaiting = reject; });
    // Web Locks already rejects on abort; this also bounds waiting in the local queue fallback.
    const onAbort = () => rejectWaiting(waiting.signal.reason || new DOMException('Cart operation canceled', 'AbortError'));
    const cancel = () => waiting.abort(signal && signal.reason);
    waiting.signal.addEventListener('abort', onAbort, { once: true });
    if (signal) {
      if (signal.aborted) cancel();
      else signal.addEventListener('abort', cancel, { once: true });
    }
    timer = root.setTimeout(() => waiting.abort(new DOMException('Cart operation lock timed out', 'AbortError')), 10000);

    function finishWaiting() {
      root.clearTimeout(timer);
      if (signal) signal.removeEventListener('abort', cancel);
      waiting.signal.removeEventListener('abort', onAbort);
    }

    async function execute() {
      if (waiting.signal.aborted) throw waiting.signal.reason || new DOMException('Cart operation canceled', 'AbortError');
      acquired = true;
      finishWaiting();
      return await task();
    }

    let work;
    try {
      if (root.navigator.locks && typeof root.navigator.locks.request === 'function') {
        work = root.navigator.locks.request('skrt-native-gift', { signal: waiting.signal }, execute);
      } else {
        work = localQueue.then(execute, execute);
        // A canceled waiter remains a no-op in the queue. It must never execute its task later.
        localQueue = work.catch(() => {});
      }
    } catch (error) {
      work = Promise.reject(error);
    }
    return Promise.race([work, aborted]).finally(() => {
      if (!acquired) finishWaiting();
    });
  }

  root.skrtCartOperations = Object.freeze({
    begin,
    end,
    run,
    get pendingIds() { return Array.from(pending); }
  });
})(typeof window !== 'undefined' ? window : null);
